import {
  csvExporter,
  dateCell,
  relationField,
  type CsvColumn,
} from '../../shared/csvExport';
import { STATUS_APPEARANCE, isKnownPlanningStatus } from './planningStatus';
import { SOURCE_CHOICES } from './recordSource';
import { TMS_ORDER_STATUS_LABELS } from './tmsOrderStatus';
import { vatExportText, vatSuggestionOf } from './vatSuggestion';
import { lostVehicleAtOf } from './markers';
import { exchangeStates, publicationLabel, STATUS_FAILED } from './publicationStatus';
import { rateParts, vehicleTypeLines } from './bankFtl';

/** Etykieta stanu jak na ekranie; kod spoza listy zostaje kodem, nie znika. */
const planningStatusText = (code: unknown): string =>
  isKnownPlanningStatus(code) ? STATUS_APPEARANCE[code].label : code == null ? '' : String(code);

/**
 * Źródło etykietą jak w filtrze banku („TMS”, „Prezlecenie”, „DEMO”) —
 * eksport zapisywał surowe kody `via-tms` / `prezlecenie` (weryfikacja 28.09).
 * Nieznane źródło zostaje kodem, brak — pustą komórką.
 */
export const sourceExportText = (code: unknown): string => {
  if (code == null || code === '') return '';
  return SOURCE_CHOICES.find((c) => c.id === code)?.name ?? String(code);
};

/**
 * Status w TMS literą z opisem („W — nieobsadzone”), jak na ekranie. Sama
 * litera nic nie mówi komuś, kto otwiera plik poza aplikacją. Nieznana
 * litera zostaje literą, brak — pustą komórką (nie kreską, to arkusz).
 */
export const tmsStatusExportText = (code: unknown): string => {
  if (typeof code !== 'string' || code === '') return '';
  const label = TMS_ORDER_STATUS_LABELS[code];
  return label ? `${code} — ${label}` : code;
};

/**
 * Stan na giełdach jak w kolumnie „Giełda” (07.10.2026): giełda, stan, od
 * kiedy w czasie polskim, a przy porażce — powód. Kilka giełd po przecinku;
 * zlecenia poza giełdą — pusta komórka (to arkusz, nie kreska).
 */
export const exchangeExportText = (record: Record<string, unknown>): string =>
  exchangeStates(record)
    .map((s) => {
      const at = dateCell(s.since);
      const when = at ? (s.status === STATUS_FAILED ? ` ${at}` : ` od ${at}`) : '';
      const reason = s.status === STATUS_FAILED && s.error ? ` (powód: ${s.error})` : '';
      return `${publicationLabel(s)}${when}${reason}`;
    })
    .join(', ');

/**
 * Kolumny eksportu banku zleceń.
 *
 * Relacje (spółka, kontrahent, przewoźnik, auto, opiekun) spłaszczone do
 * nazw — domyślny eksport oddawał je jako puste kolumny. Pola jak
 * w `LIST_INCLUDE` API banku.
 */
export const BANK_EXPORT_COLUMNS: readonly CsvColumn[] = [
  { header: 'Nr zlecenia', value: (r) => r.externalId ?? `#${String(r.id)}` },
  { header: 'Źródło', value: (r) => sourceExportText(r.sourceSystem) },
  { header: 'Nr referencyjny TMS', value: (r) => r.tmsReferenceNumber },
  { header: 'Kraj załadunku', value: (r) => r.fromCountry },
  { header: 'Kod załadunku', value: (r) => r.fromPostalCode },
  { header: 'Miasto załadunku', value: (r) => r.fromCity },
  { header: 'Kraj rozładunku', value: (r) => r.toCountry },
  { header: 'Kod rozładunku', value: (r) => r.toPostalCode },
  { header: 'Miasto rozładunku', value: (r) => r.toCity },
  { header: 'Załadunek od', value: (r) => dateCell(r.fromDate) },
  { header: 'Rozładunek do', value: (r) => dateCell(r.toDate) },
  { header: 'Stan planowania', value: (r) => planningStatusText(r.planningStatus) },
  { header: 'Status w TMS', value: (r) => tmsStatusExportText(r.tmsOrderStatus) },
  { header: 'Pojazd', value: (r) => relationField(r.assignedCar, 'registrationNumber') },
  { header: 'Przewoźnik', value: (r) => relationField(r.carrier, 'name') },
  { header: 'Kontrahent', value: (r) => relationField(r.contractor, 'legalName') },
  { header: 'Spółka', value: (r) => relationField(r.company, 'name') },
  { header: 'Opiekun', value: (r) => relationField(r.assignedToUser, 'username') },
  // „Bezczynne” jak filtr listy, kafelek pulpitu i licznik w pasku (poprawa
  // brzmienia 01.10.2026; wcześniej „Bez działania po progu”).
  { header: 'Bezczynne', value: (r) => r.idle },
  // Rekomendowana stawka VAT i czerwona lista (omówienie 01.10.2026) — te
  // same pola co w kolumnach „VAT” i „Znaczniki”.
  { header: 'Rekomendowany VAT', value: (r) => vatExportText(vatSuggestionOf(r)) },
  { header: 'Straciło pojazd', value: (r) => dateCell(lostVehicleAtOf(r)) },
  // Kolumna „Giełda” listy (prezentacja 02.10, pkt 9).
  { header: 'Giełda', value: (r) => exchangeExportText(r) },
  { header: 'Utworzone', value: (r) => dateCell(r.createdAt) },
];

export const bankExporter = csvExporter(BANK_EXPORT_COLUMNS, 'zlecenia');

/**
 * Eksport bez kolumny „Giełda” — jak na produkcji 0.31.3, gdy przełącznik
 * `exchangeColumn` jest wyłączony (wersja „obecna”; decyzja użytkownika
 * 12.10.2026). Reszta kolumn i kolejność bez zmian.
 */
export const BANK_EXPORT_COLUMNS_WITHOUT_EXCHANGE: readonly CsvColumn[] =
  BANK_EXPORT_COLUMNS.filter((c) => c.header !== 'Giełda');

export const bankExporterWithoutExchange = csvExporter(
  BANK_EXPORT_COLUMNS_WITHOUT_EXCHANGE,
  'zlecenia',
);

/**
 * Kolumny „Stawka”, „Waluta”, „Typ pojazdu” i „Wymagania” — te same co na
 * liście z przełącznikiem `bankRateTypeColumns` (S-24.10-13, odpowiedź
 * z 21.10.2026 na pytanie 20). W arkuszu kwota jako liczba z przecinkiem,
 * jak na ekranie; brak — pusta komórka (to arkusz, nie kreska).
 */
export const RATE_TYPE_EXPORT_COLUMNS: readonly CsvColumn[] = [
  { header: 'Stawka', value: (r) => rateParts(r.price, r.currency)?.amount ?? '' },
  { header: 'Waluta', value: (r) => rateParts(r.price, r.currency)?.currency ?? '' },
  { header: 'Typ pojazdu', value: (r) => vehicleTypeLines(r).type ?? '' },
  { header: 'Wymagania', value: (r) => vehicleTypeLines(r).requirements ?? '' },
];

/** Kolumny eksportu dla stanu przełączników listy — nowe po „Kontrahencie”. */
export function bankExportColumns(exchange: boolean, rateType: boolean): readonly CsvColumn[] {
  const base = exchange ? BANK_EXPORT_COLUMNS : BANK_EXPORT_COLUMNS_WITHOUT_EXCHANGE;
  if (!rateType) return base;
  const at = base.findIndex((c) => c.header === 'Kontrahent') + 1;
  return [...base.slice(0, at), ...RATE_TYPE_EXPORT_COLUMNS, ...base.slice(at)];
}

/** Eksport z osobnymi kolumnami stawki i typu (z „Giełdą” albo bez). */
export const bankExporterWithRateType = csvExporter(bankExportColumns(true, true), 'zlecenia');
export const bankExporterWithRateTypeWithoutExchange = csvExporter(
  bankExportColumns(false, true),
  'zlecenia',
);

/** Eksport listy dla stanu przełączników. */
export function bankExporterFor(exchange: boolean, rateType: boolean) {
  if (rateType) return exchange ? bankExporterWithRateType : bankExporterWithRateTypeWithoutExchange;
  return exchange ? bankExporter : bankExporterWithoutExchange;
}
